import { useState, useEffect } from "react";
import { useFirebase } from "@/contexts/FirebaseContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { getEnrollmentsByInstructorId, updatePracticalLessonProgress } from "@/utils/firestoreService";
import { Enrollment } from "@/types";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import { Clock, MessageSquare, Search, User, UserX, Eye } from "lucide-react";

type MergedEnrollment = Enrollment;

const InstructorStudents = () => {
  const { currentUser } = useFirebase();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState<MergedEnrollment[]>([]);
  const [filteredEnrollments, setFilteredEnrollments] = useState<MergedEnrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEnrollment, setSelectedEnrollment] = useState<MergedEnrollment | null>(null);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [hoursToAdd, setHoursToAdd] = useState<number>(1);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedStudentDetails, setSelectedStudentDetails] = useState<MergedEnrollment | null>(null);

  useEffect(() => {
    const fetchStudentEnrollments = async () => {
      if (!currentUser?.uid) {
        console.log("No user ID found, cannot fetch enrollments");
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        console.log("Fetching enrollments for instructor with ID:", currentUser.uid);
        const fetchedEnrollments = await getEnrollmentsByInstructorId(currentUser.uid);
        console.log("Fetched instructor enrollments:", fetchedEnrollments);
        
        const validEnrollments = fetchedEnrollments.filter(enrollment => 
          enrollment && enrollment.studentName && enrollment.courseName
        );

        const convertedEnrollments: MergedEnrollment[] = validEnrollments.map(enrollment => {
          if (!enrollment.createdAt) {
            enrollment.createdAt = new Date().toISOString();
          }
          
          return {
            ...enrollment,
            paymentStatus: enrollment.paymentStatus as any,
            enrollmentStatus: enrollment.enrollmentStatus as any,
          } as MergedEnrollment;
        });
        
        setEnrollments(convertedEnrollments);
        setFilteredEnrollments(convertedEnrollments);
      } catch (error) {
        console.error("Error fetching student enrollments:", error);
        toast({
          title: "Error",
          description: "Failed to load your students. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchStudentEnrollments();
    }
  }, [currentUser, toast]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = enrollments.filter(
        enrollment =>
          enrollment.studentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          enrollment.courseName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredEnrollments(filtered);
    } else {
      setFilteredEnrollments(enrollments);
    }
  }, [searchTerm, enrollments]);

  const handleUpdateProgress = async () => {
    if (!selectedEnrollment || !selectedEnrollment.id) return;
    
    try {
      console.log("Updating progress for enrollment:", selectedEnrollment.id, "Hours to add:", hoursToAdd);
      const updatedEnrollment = await updatePracticalLessonProgress(
        selectedEnrollment.id, 
        hoursToAdd
      );
      
      if (updatedEnrollment) {
        console.log("Progress updated successfully:", updatedEnrollment);
        setEnrollments(enrollments.map(e => 
          e.id === selectedEnrollment.id ? {
            ...e,
            hoursTaken: updatedEnrollment.hoursTaken,
            hoursRemaining: updatedEnrollment.hoursRemaining
          } : e
        ));
        
        toast({
          title: "Progress Updated",
          description: `Added ${hoursToAdd} hours to ${selectedEnrollment.studentName}'s progress`,
        });
      }
      
      setProgressDialogOpen(false);
    } catch (error) {
      console.error("Error updating progress:", error);
      toast({
        title: "Update Failed",
        description: "Could not update student progress",
        variant: "destructive"
      });
    }
  };

  const openProgressDialog = (enrollment: MergedEnrollment) => {
    setSelectedEnrollment(enrollment);
    setHoursToAdd(1);
    setProgressDialogOpen(true);
  };

  const getProgressPercentage = (enrollment: MergedEnrollment) => {
    if (!enrollment.totalHours) {
      return 0;
    }
    
    const hoursTaken = enrollment.hoursTaken || 0;
    return Math.min(100, Math.round((hoursTaken / enrollment.totalHours) * 100));
  };

  const openDetailsDialog = (enrollment: MergedEnrollment) => {
    setSelectedStudentDetails(enrollment);
    setDetailsDialogOpen(true);
  };

  return (
    <DashboardLayout role="instructor">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">My Students</h1>
          <p className="text-gray-500 mt-2">Manage your assigned students and track their progress</p>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Assigned Students</CardTitle>
            <div className="flex items-center space-x-2 mt-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  type="search"
                  placeholder="Search students..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center my-8">
                <p className="text-gray-500">Loading students...</p>
              </div>
            ) : filteredEnrollments.length === 0 ? (
              <div className="text-center py-12">
                <UserX className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium">No students found</h3>
                <p className="text-gray-500 mt-2">
                  {searchTerm 
                    ? "No students match your search criteria"
                    : "You don't have any assigned students yet"}
                </p>
              </div>
            ) : (
              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Course</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead>Payment Status</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEnrollments.map((enrollment) => (
                      <TableRow key={enrollment.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{enrollment.studentName}</div>
                            <div className="text-sm text-gray-500">{enrollment.studentEmail}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            {enrollment.courseName}
                            <Badge className="ml-2" variant="outline">
                              {enrollment.courseType || "N/A"}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          {enrollment.totalHours ? (
                            <div className="space-y-1">
                              <div className="flex justify-between text-xs">
                                <span>
                                  {enrollment.hoursTaken || 0}/{enrollment.totalHours} hrs
                                </span>
                                <span>{getProgressPercentage(enrollment)}%</span>
                              </div>
                              <Progress value={getProgressPercentage(enrollment)} className="h-2" />
                            </div>
                          ) : (
                            <span className="text-gray-500">N/A</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={enrollment.paymentToInstructor === 'paid' ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600'}>
                            {enrollment.paymentToInstructor === 'paid' ? 'Paid' : 'Unpaid'}
                          </Badge>
                          <div className="text-xs text-gray-500 mt-1">
                            Student paid: {enrollment.totalAmount ? `£${enrollment.totalAmount}` : 'N/A'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={
                              enrollment.status === "completed" ? "bg-green-500" :
                              enrollment.status === "assigned" ? "bg-blue-500" : 
                              "bg-yellow-500"
                            }
                          >
                            {enrollment.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openDetailsDialog(enrollment)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View Details
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate("/instructor-dashboard/messages", {
                                state: { studentId: enrollment.studentId || enrollment.userId }
                              })}
                            >
                              <MessageSquare className="h-4 w-4 mr-1" />
                              Message
                            </Button>
                            
                            {enrollment.totalHours && (
                              <Button
                                size="sm"
                                onClick={() => openProgressDialog(enrollment)}
                              >
                                <Clock className="h-4 w-4 mr-1" />
                                Update Hours
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Student Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Student Details</DialogTitle>
            <DialogDescription>
              Detailed information about {selectedStudentDetails?.studentName}'s enrollment
            </DialogDescription>
          </DialogHeader>

          {selectedStudentDetails && (
            <div className="space-y-6 py-4">
              {/* Student Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Student Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-medium">{selectedStudentDetails.studentName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{selectedStudentDetails.studentEmail}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium">{selectedStudentDetails.studentPhone || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Address</p>
                    <p className="font-medium">{selectedStudentDetails.studentAddress || 'Not provided'}</p>
                  </div>
                </div>
              </div>

              {/* Course Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Course Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Course Name</p>
                    <p className="font-medium">{selectedStudentDetails.courseName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Course Type</p>
                    <p className="font-medium">{selectedStudentDetails.courseType || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Start Date</p>
                    <p className="font-medium">
                      {selectedStudentDetails.startDate ? new Date(selectedStudentDetails.startDate).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Preferred Time</p>
                    <p className="font-medium">{selectedStudentDetails.preferredTime || 'To be scheduled'}</p>
                  </div>
                </div>
              </div>

              {/* Progress Information */}
              {selectedStudentDetails.totalHours && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Progress Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Total Hours</p>
                      <p className="font-medium">{selectedStudentDetails.totalHours} hours</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Hours Taken</p>
                      <p className="font-medium">{selectedStudentDetails.hoursTaken || 0} hours</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Hours Remaining</p>
                      <p className="font-medium">
                        {selectedStudentDetails.hoursRemaining || 
                          (selectedStudentDetails.totalHours - (selectedStudentDetails.hoursTaken || 0))} hours
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Progress</p>
                      <div className="mt-1">
                        <Progress value={getProgressPercentage(selectedStudentDetails)} className="h-2" />
                        <p className="text-xs text-gray-500 mt-1">
                          {getProgressPercentage(selectedStudentDetails)}% Complete
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Additional Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Driving Experience</p>
                    <p className="font-medium">{selectedStudentDetails.drivingExperience || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Vehicle Type</p>
                    <p className="font-medium">{selectedStudentDetails.vehicleType || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">License Type</p>
                    <p className="font-medium">{selectedStudentDetails.licenseType || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Previous Lessons</p>
                    <p className="font-medium">{selectedStudentDetails.previousLessons || '0'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Medical Conditions</p>
                    <p className="font-medium">{selectedStudentDetails.medicalConditions ? 'Yes' : 'No'}</p>
                    {selectedStudentDetails.medicalConditions && selectedStudentDetails.medicalDetails && (
                      <p className="text-sm text-gray-500 mt-1">{selectedStudentDetails.medicalDetails}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Nervous About Driving</p>
                    <p className="font-medium">{selectedStudentDetails.nervousAboutDriving ? 'Yes' : 'No'}</p>
                  </div>
                  {selectedStudentDetails.specificGoals && (
                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-500">Specific Goals</p>
                      <p className="font-medium">{selectedStudentDetails.specificGoals}</p>
                    </div>
                  )}
                  {selectedStudentDetails.focusAreas && (
                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-500">Focus Areas</p>
                      <p className="font-medium">{selectedStudentDetails.focusAreas}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Payment Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Total Amount</p>
                    <p className="font-medium">£{selectedStudentDetails.amount || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Payment Status</p>
                    <Badge variant="outline" className={selectedStudentDetails.paymentToInstructor === 'paid' ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600'}>
                      {selectedStudentDetails.paymentToInstructor === 'paid' ? 'Paid' : 'Unpaid'}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={progressDialogOpen} onOpenChange={setProgressDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Student Progress</DialogTitle>
            <DialogDescription>
              Record hours taken by {selectedEnrollment?.studentName} for {selectedEnrollment?.courseName}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current Progress</Label>
              <div className="text-sm">
                <p><strong>Hours Taken:</strong> {selectedEnrollment?.hoursTaken || 0} hrs</p>
                <p><strong>Hours Remaining:</strong> {selectedEnrollment?.hoursRemaining || selectedEnrollment?.totalHours || 0} hrs</p>
                <p><strong>Total Course Hours:</strong> {selectedEnrollment?.totalHours || 0} hrs</p>
              </div>
              
              {selectedEnrollment && (
                <div className="mt-2">
                  <Progress value={getProgressPercentage(selectedEnrollment)} className="h-2" />
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="hours-to-add">Hours to Add</Label>
              <Input
                id="hours-to-add"
                type="number"
                min={0.5}
                step={0.5}
                max={(selectedEnrollment?.hoursRemaining || 0) > 0 ? selectedEnrollment?.hoursRemaining : 0}
                value={hoursToAdd}
                onChange={(e) => setHoursToAdd(parseFloat(e.target.value) || 0)}
              />
              {hoursToAdd > (selectedEnrollment?.hoursRemaining || 0) && (
                <p className="text-red-500 text-xs">
                  Value exceeds remaining hours ({selectedEnrollment?.hoursRemaining || 0} hrs)
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setProgressDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateProgress}
              disabled={hoursToAdd <= 0 || hoursToAdd > (selectedEnrollment?.hoursRemaining || 0)}
            >
              Update Progress
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default InstructorStudents;
